import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "../../Styles/LeaveScreen.css";

import { apiPost } from "../../Api/Client.js";
import { Panel } from "../../Shared/ScreenComponents.jsx";
import { isoDate } from "../../Shared/ScreenUtils.jsx";

export function LeaveApplyScreen({ data, selectedEmployeeId, reload }) {
  const [form, setForm] = useState({ employee_id: selectedEmployeeId || "", leave_type: "Paid", starts_on: isoDate(new Date()), ends_on: isoDate(new Date()), reason: "" });
  const [result, setResult] = useState(null);
  // One request at a time: a double-click or a slow network must not send the same leave twice.
  const [submitting, setSubmitting] = useState(false);
  const currentEmployeeId = selectedEmployeeId || data.me?.employees?.[0]?.id || form.employee_id;
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  const calendarDays = useMemo(() => {
    const days = [];
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const today = new Date();
    const myLeaves = (data.leaveRequests || []).filter((lr) => String(lr.employee || lr.employee_id) === String(currentEmployeeId));
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(calYear, calMonth, d);
      const iso = isoDate(date);
      const leaves = myLeaves.filter((lr) => iso >= String(lr.starts_on).slice(0, 10) && iso <= String(lr.ends_on).slice(0, 10));
      days.push({ day: d, iso, isToday: iso === isoDate(today), isPast: date < new Date(today.getFullYear(), today.getMonth(), today.getDate()), leaves });
    }
    return days;
  }, [calMonth, calYear, data.leaveRequests, currentEmployeeId]);

  // Leave is always filed for the signed-in person's own employee record. The
  // company-wide employee list is never a fallback: its first row is somebody else.
  useEffect(() => {
    setForm((current) => ({ ...current, employee_id: selectedEmployeeId || "" }));
  }, [selectedEmployeeId]);

  const submit = async () => {
    if (submitting) return;
    // Validate Dates
    if (!form.starts_on || !form.ends_on) {
      setResult({ error: "Start Date And End Date Are Required." });
      return;
    }
    if (form.ends_on < form.starts_on) {
      setResult({ error: "End Date Cannot Be Earlier Than Start Date." });
      return;
    }
    // Backdated leave is allowed on purpose. People fall ill and apply
    // afterwards; refusing that just meant the record never got created.
    setSubmitting(true);
    try {
      await apiPost("/MainApp/leave/apply/", form);
      setResult({ success: "Leave Request Submitted Successfully." });
      // Sent: clear the form so the same request is not sent again by a second click.
      setForm((current) => ({ ...current, starts_on: isoDate(new Date()), ends_on: isoDate(new Date()), reason: "" }));
      reload(["leaveRequests", "leaveBalances", "leaveOverview", "notifications", "employees"]);
    } catch (err) {
      const payload = err?.payload || {};
      const errorMsg = payload.detail || payload.dates || payload.employee || payload.permission || payload.wallet || err?.message || "Failed To Apply Leave.";
      setResult({ error: typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="Leave-Screen Screen-Stack">
      <section className="Page-Heading"><div><span>Leave</span><h1>Apply Leave</h1></div></section>
      <section>
        {/* Three fields, nothing else. Your own name was read-only text you
            could not change, and the leave type is decided by employment
            status rather than chosen — so neither belonged on a form. */}
        <Panel title="Leave Request">
          <div className="Leave-Form">
            <div className="Form-Grid Two">
              <label>Starts On<input type="date" value={form.starts_on} onChange={(event) => setForm({ ...form, starts_on: event.target.value })} /></label>
              <label>Ends On<input type="date" value={form.ends_on} onChange={(event) => setForm({ ...form, ends_on: event.target.value })} /></label>
            </div>
            <label className="Leave-Reason">
              Reason
              <textarea
                value={form.reason}
                onChange={(event) => setForm({ ...form, reason: event.target.value })}
                placeholder="Why are you taking this leave?"
                rows={4}
              />
            </label>
            <div className="Leave-Form-Foot">
              <button className="Primary-Button" onClick={submit} disabled={submitting || !form.employee_id || !form.starts_on || !form.ends_on}>{submitting ? "Submitting…" : "Submit Leave"}</button>
            </div>
            {data.me && !selectedEmployeeId && <div className="Leave-Note Error">Your account is not linked to an employee record, so leave cannot be applied from it.</div>}
            {result?.error && <div className="Leave-Note Error">{result.error}</div>}
            {result?.success && <div className="Leave-Note Success">{result.success}</div>}
          </div>
        </Panel>
      </section>
      <Panel title="Leave Calendar" subtitle={`${calMonth + 1}/${calYear}`}
        right={
          <span style={{ display: "flex", gap: 6 }}>
            <button className="Soft-Button Small" onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}><ChevronLeft size={14} /></button>
            <button className="Soft-Button Small" onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}><ChevronRight size={14} /></button>
          </span>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, textAlign: "center" }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <strong key={d} style={{ fontSize: 11, color: "var(--c-500)", padding: "4px 0" }}>{d}</strong>)}
          {calendarDays.map((day, i) => !day ? <span key={i} /> : (
            <div key={i} style={{ padding: "6px 0", borderRadius: 6, fontSize: 12, fontWeight: day.isToday ? 700 : 400, background: day.isToday ? "var(--blue-50)" : (day.leaves || []).length > 0 ? "var(--red-50)" : "transparent", color: (day.leaves || []).length > 0 ? "var(--red-600)" : day.isPast ? "var(--c-400)" : "var(--c-900)" }}>
              <div>{day.day}</div>
              {(day.leaves || []).length > 0 && <div style={{ fontSize: 9, color: "var(--red-600)" }}>{(day.leaves || []).length} Leave{day.leaves?.length > 1 ? "s" : ""}</div>}
            </div>
          ))}
        </div>
      </Panel>
    </section>
  );
}
